import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import { useAttachments } from 'proposals/attachments/useAttachments'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import CloseIcon from '@mui/icons-material/Close'
import { CircularProgress } from '@mui/material'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import { useDate } from 'utils/useDate'

const InfoArticle = styled('article')(({ theme: { spacing } }) => ({
  '&:not(:first-of-type)': {
    marginTop: spacing(3)
  }
}))

type AttachmentsPropTypes = {
  contextToken: string
  resourceWebLinks: ResourceWebLink[]
}

const Attachments: React.FC<AttachmentsPropTypes> = ({ contextToken, resourceWebLinks }) => {
  const { t } = useTranslation()
  const [modalImage, setModalImage] = useState<string>('')
  const { formatDate } = useDate()

  const {
    models: { images, documents, isImagesLoading, isDocumentsLoading, isArchiveLoading },
    operations: { getArchive }
  } = useAttachments(contextToken)

  const closeModal = (): void => {
    setModalImage('')
  }

  if (isImagesLoading || isDocumentsLoading) {
    return <CircularProgress sx={{ mt: 1 }} size={24} />
  }

  if (images.length === 0 && documents.length === 0 && resourceWebLinks.length === 0) {
    return null
  }

  return (
    <InfoArticle>
      <div>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, 120px)', gap: '8px' }}>
          {images.map((image) => (
            <Box key={image.id} sx={{ mb: 3 }}>
              <Box
                component="img" 
                src={image.smallThumbNailUrl}
                onClick={() => setModalImage(image.url)}
                alt="attachment preview"
                sx={{
                  height: 120,
                  width: 120,
                  cursor: 'pointer',
                  borderRadius: 1
                }}
              />
              <Typography variant="body2" color="textSecondary">
                {formatDate(image.uploaded, 'dd.MM.yyyy HH:mm')}
              </Typography>
            </Box>
          ))}
        </Box>

        {documents.length > 0 && (
          <Box sx={{ maxWidth: 450, mb: 2 }}>
            <Typography sx={{ lineHeight: 1.2 }} variant="body2" color="textSecondary" component="h6">
              {t('attachments')}
            </Typography>
            {documents.map((document) => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} key={document.id}>
                <Box>
                  <Typography>{document.originalName}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(document.uploaded, 'dd.MM.yyyy HH:mm')}
                  </Typography>
                </Box>
                <Button color="primary" component="a" href={document.url} target="_blank">
                  {t('download')}
                </Button>
              </Box>
            ))}
            {documents.length + images.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', maxWidth: '450px' }}>
                <Button color="primary" onClick={getArchive} disabled={isArchiveLoading}>
                  {t('download-all')}
                </Button>
              </Box>
            )}
          </Box>
        )}
        {resourceWebLinks.length > 0 && (
          <Box sx={{ maxWidth: 450, mb: 2 }}>
            <Typography sx={{ lineHeight: 1.2 }} variant="body2" color="textSecondary" component="h6">
              {t('links')}
            </Typography>
            {resourceWebLinks.map((link) => (
              <Box sx={{ mb: 1 }} key={link.id}>
                <Typography>
                  <Link
                    target="_blank"
                    rel="noopener"
                    href={
                      link.url.startsWith('http://') || link.url.startsWith('https://') ? link.url : `//${link.url}`
                    }
                    underline="hover"
                  >
                    {link.name ? link.name : link.url}
                  </Link>
                </Typography>
                {link.description && <Typography variant="body2">{link.description}</Typography>}
              </Box>
            ))}
          </Box>
        )}
      </div>
      <Modal open={!!modalImage} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box sx={{ position: 'relative', pointerEvents: 'auto' }}>
            <Box sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
              <IconButton onClick={closeModal} size="large">
                <CloseIcon sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderRadius: 1 }} />
              </IconButton>
            </Box>
            <Box
              component="img"
              src={modalImage}
              alt="attachment"
              sx={{
                objectFit: 'contain',
                maxWidth: '90vw',
                maxHeight: '90vh',
                width: 'auto',
                height: 'auto',
                position: 'relative'
              }}
            />
          </Box>
        </Box>
      </Modal>
    </InfoArticle>
  )
}

export default Attachments
